import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Header } from '@/components';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useRoles } from '@/hooks/useRoles';
import { usePermissions } from '@/permissions/usePermissions';
import { useAuth } from '@/store/auth/auth.store';
import { useDashboardSettingsQuery, useSaveDashboardSettingsMutation } from '@/store/dashboard';
import { getDefaultLayout } from '@/utils/dashboard.defaults';
import { getWidgetsForRights } from '@/utils/dashboard.registry';
import type { WidgetCategory, WidgetId, WidgetLayoutItem } from '@/utils/dashboard.types';
import { getActiveDashboardType } from '@/utils/dashboard.types';
import { ImpactStyle, triggerHaptic } from '@/utils/haptics';
import { toast } from '@/utils/toast';

import { DashboardSettingsDesktopPage } from './components/DashboardSettingsDesktopPage';
import { DashboardSettingsMobilePage } from './components/DashboardSettingsMobilePage';

type Platform = 'desktop' | 'mobile';

const GRID_COLS = 4;
const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 8 } };
const TOUCH_SENSOR_OPTIONS = { activationConstraint: { delay: 120, tolerance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };
const NOOP_DRAG_OVER = (_event: DragOverEvent) => {};

// Returns the start/end indices of the row that contains targetIndex.
function getRowBounds(
  items: WidgetLayoutItem[],
  targetIndex: number,
): { start: number; end: number } {
  const rows: number[] = new Array(items.length);
  let colsUsed = 0;
  let currentRow = 0;
  for (let i = 0; i < items.length; i++) {
    const span = items[i].col_span;
    if (colsUsed + span > GRID_COLS) {
      currentRow++;
      colsUsed = 0;
    }
    rows[i] = currentRow;
    colsUsed += span;
    if (colsUsed >= GRID_COLS) {
      colsUsed = 0;
      currentRow++;
    }
  }
  const targetRow = rows[targetIndex];
  return { start: rows.indexOf(targetRow), end: rows.lastIndexOf(targetRow) };
}

function buildDraftLayout(
  layoutWidgets: WidgetLayoutItem[],
  availableWidgets: ReturnType<typeof getWidgetsForRights>,
): WidgetLayoutItem[] {
  const filteredWidgets = layoutWidgets.filter((widget) =>
    availableWidgets.some((availableWidget) => availableWidget.id === widget.id),
  );
  const existingIds = new Set(filteredWidgets.map((widget) => widget.id));
  const nextOrder = filteredWidgets.length;

  const missingWidgets = availableWidgets
    .filter((widget) => !existingIds.has(widget.id))
    .map((widget, index) => ({
      id: widget.id,
      visible: false,
      order: nextOrder + index,
      col_span: widget.defaultColSpan,
    }));

  return [...filteredWidgets, ...missingWidgets];
}

export default function DashboardSettingsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { userRoles } = useRoles();
  const { rights } = usePermissions();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const userId = useAuth((state) => state.session?.id);
  const { data: settings, isLoading } = useDashboardSettingsQuery(userId);
  const { mutateAsync: saveSettings, isPending } = useSaveDashboardSettingsMutation(userId);
  const loading = isLoading || isPending;

  const [platform, setPlatform] = useState<Platform>('desktop');
  const [draft, setDraft] = useState<WidgetLayoutItem[]>([]);
  const [saved, setSaved] = useState<WidgetLayoutItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overlayWidth, setOverlayWidth] = useState<number | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const dashboardType = getActiveDashboardType(userRoles ?? []);
  const availableWidgets = useMemo(
    () => getWidgetsForRights(rights, userRoles ?? []),
    [rights, userRoles],
  );
  const availableIds = useMemo(
    () => new Set(availableWidgets.map((widget) => widget.id)),
    [availableWidgets],
  );
  const currentPlatform: Platform = isMobile ? 'mobile' : platform;

  useEffect(() => {
    const savedLayout = settings?.dashboards?.[dashboardType];
    const layout = savedLayout ?? getDefaultLayout(dashboardType);
    const baseWidgets = layout[currentPlatform].widgets;

    const platformWidgets = buildDraftLayout(baseWidgets, availableWidgets).filter((w) =>
      availableIds.has(w.id),
    );

    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setDraft(platformWidgets);
      setSaved(platformWidgets);
    });

    return () => {
      cancelled = true;
    };
  }, [settings, dashboardType, currentPlatform, availableWidgets, availableIds]);

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor, TOUCH_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveId(id);
    triggerHaptic(ImpactStyle.Medium);
    if (gridRef.current) {
      if (currentPlatform === 'mobile') {
        // Single-column layout: the dragged card spans the full grid width.
        setOverlayWidth(gridRef.current.clientWidth);
      } else {
        const item = draft.find((w) => w.id === id);
        if (item) {
          const cellWidth = (gridRef.current.clientWidth - 3 * 12) / 4;
          setOverlayWidth(item.col_span * cellWidth + (item.col_span - 1) * 12);
        }
      }
    }
  };

  const handleDesktopDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (
      over &&
      active.id !== over.id &&
      !lockedIds.has(active.id as WidgetId) &&
      !lockedIds.has(over.id as WidgetId)
    ) {
      triggerHaptic(ImpactStyle.Light);
      setDraft((prev) => {
        const oldIndex = prev.findIndex((w) => w.id === active.id);
        const newIndex = prev.findIndex((w) => w.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return prev;
        const draggedItem = prev[oldIndex];

        if (draggedItem.col_span === GRID_COLS) {
          const withoutDragged = prev.filter((_, i) => i !== oldIndex);
          const overIndexInReduced = withoutDragged.findIndex((w) => w.id === over.id);
          const { start, end } = getRowBounds(withoutDragged, overIndexInReduced);
          const insertAt = oldIndex < newIndex ? end + 1 : start;
          if (insertAt === oldIndex) return prev;
          const result = [...withoutDragged];
          result.splice(insertAt, 0, draggedItem);
          if (result.every((widget, index) => widget.id === prev[index]?.id)) return prev;
          return result.map((w, i) => ({ ...w, order: i }));
        }

        const targetItem = prev[newIndex];
        let adjustedIndex = newIndex;
        if (targetItem.col_span === GRID_COLS && newIndex > oldIndex) {
          adjustedIndex = newIndex - 1;
        }
        if (adjustedIndex === oldIndex) return prev;
        const moved = arrayMove(prev, oldIndex, adjustedIndex);
        if (moved.every((widget, index) => widget.id === prev[index]?.id)) return prev;
        return moved.map((w, i) => ({ ...w, order: i }));
      });
    }

    setActiveId(null);
    setOverlayWidth(null);
  };

  const handleMobileDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      triggerHaptic(ImpactStyle.Light);
      setDraft((prev) => {
        const visible = prev.filter((w) => w.visible).sort((a, b) => a.order - b.order);
        const hidden = prev.filter((w) => !w.visible).sort((a, b) => a.order - b.order);
        const lockedVisible = visible.filter((w) => lockedIds.has(w.id));
        const sortableVisible = visible.filter((w) => !lockedIds.has(w.id));
        const oldIndex = sortableVisible.findIndex((w) => w.id === active.id);
        const newIndex = sortableVisible.findIndex((w) => w.id === over.id);

        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev;

        const reorderedSortable = arrayMove(sortableVisible, oldIndex, newIndex);
        const composed = [...lockedVisible, ...reorderedSortable, ...hidden];
        return composed.map((w, i) => ({ ...w, order: i }));
      });
    }

    setActiveId(null);
    setOverlayWidth(null);
  };

  const handleToggle = (id: string) => {
    if (lockedIds.has(id as WidgetId)) return;
    setDraft((prev) => prev.map((w) => (w.id === id ? { ...w, visible: !w.visible } : w)));
  };

  const handleSave = async () => {
    if (!userId) return;

    const currentLayout = settings?.dashboards?.[dashboardType] ?? getDefaultLayout(dashboardType);
    const otherPlatform: Platform = currentPlatform === 'desktop' ? 'mobile' : 'desktop';

    const newSettings = {
      dashboards: {
        ...(settings?.dashboards ?? {}),
        [dashboardType]: {
          ...currentLayout,
          [currentPlatform]: { widgets: draft },
          [otherPlatform]: currentLayout[otherPlatform],
        },
      },
    };

    try {
      await saveSettings(newSettings);
      setSaved(draft);
      toast.success('Dashboard settings saved');
      navigate('/dashboard');
    } catch {
      toast.error('Couldn’t save your dashboard settings. Please try again.');
    }
  };

  const handleCancel = () => {
    setDraft(saved);
  };

  const groupedWidgets = availableWidgets.reduce<Record<WidgetCategory, WidgetLayoutItem[]>>(
    (acc, def) => {
      const item = draft.find((w) => w.id === def.id);
      if (!item) return acc;
      if (!acc[def.category]) acc[def.category] = [];
      acc[def.category].push(item);
      return acc;
    },
    {} as Record<WidgetCategory, WidgetLayoutItem[]>,
  );

  const lockedIds = new Set<WidgetId>(
    availableWidgets
      .filter((w) => (currentPlatform === 'mobile' ? w.lockedOnMobile : w.lockedOnDesktop))
      .map((w) => w.id),
  );

  const visibleItems = useMemo(
    () => draft.filter((w) => w.visible).sort((a, b) => a.order - b.order),
    [draft],
  );
  const lockedVisibleItems = visibleItems.filter((w) => lockedIds.has(w.id));
  const sortableVisibleItems = visibleItems.filter((w) => !lockedIds.has(w.id));
  const sortableVisibleIds = sortableVisibleItems.map((w) => w.id);
  const hiddenWidgetsCount = draft.length - visibleItems.length;

  const isDirty = useMemo(() => {
    if (draft.length !== saved.length) return true;
    const savedById = new Map(saved.map((w) => [w.id, w]));
    return draft.some((w) => {
      const prev = savedById.get(w.id);
      return (
        !prev ||
        prev.visible !== w.visible ||
        prev.order !== w.order ||
        prev.col_span !== w.col_span
      );
    });
  }, [draft, saved]);

  return (
    <>
      {!embedded && <Header title="My Dashboard Settings" backButton />}
      {isMobile ? (
        <DashboardSettingsMobilePage
          groupedWidgets={groupedWidgets}
          lockedVisibleItems={lockedVisibleItems}
          sortableVisibleItems={sortableVisibleItems}
          sortableVisibleIds={sortableVisibleIds}
          lockedIds={lockedIds}
          activeId={activeId}
          isAddModalOpen={isAddModalOpen}
          hiddenWidgetsCount={hiddenWidgetsCount}
          loading={loading}
          sensors={sensors}
          onToggle={handleToggle}
          onDragStart={handleDragStart}
          onDragOver={NOOP_DRAG_OVER}
          onDragEnd={handleMobileDragEnd}
          onOpenAddModal={() => setIsAddModalOpen(true)}
          onCloseAddModal={() => setIsAddModalOpen(false)}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      ) : (
        <DashboardSettingsDesktopPage
          platform={platform}
          setPlatform={setPlatform}
          groupedWidgets={groupedWidgets}
          lockedVisibleItems={lockedVisibleItems}
          sortableVisibleItems={sortableVisibleItems}
          sortableVisibleIds={sortableVisibleIds}
          lockedIds={lockedIds}
          activeId={activeId}
          overlayWidth={overlayWidth}
          gridRef={gridRef}
          sensors={sensors}
          dirty={isDirty}
          onToggle={handleToggle}
          onDragStart={handleDragStart}
          onDragOver={NOOP_DRAG_OVER}
          onDragEnd={handleDesktopDragEnd}
          loading={loading}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      )}
    </>
  );
}
